// src/App.tsx
import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import SupportChatWidget from "./components/SupportChatWidget";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
import Home from "./pages/Home";
import Login from "./pages/Login";
import LoginWithCode from "./pages/LoginWithCode";
import Dashboard from "./pages/Dashboard";
import Application from "./pages/Application";
import BasicInformation from "./pages/BasicInformation";
import HousingHistory from "./pages/HousingHistory";
import IdentityVerification from "./pages/IdentityVerification";
import Financial from "./pages/Financial";
import Documents from "./pages/Documents";
import Profile from "./pages/Profile";
import PaymentMethods from "./pages/PaymentMethods";
import IncomeSources from "./pages/IncomeSources";
import ClaimDetails from "./pages/ClaimDetails";
import ClaimPayment from "./pages/ClaimPayment";
import PolicyRenewal from "./pages/PolicyRenewal";
import PolicyChangePayment from "./pages/PolicyChangePayment";
import FAQ from "./pages/FAQ";
import PaymentPage from "./pages/PaymentPage";
import SDAIdentity from "./pages/sda/SDAIdentity";
import SDAFinancial from "./pages/sda/SDAFinancial";
import SDAUnderwriting from "./pages/sda/SDAUnderwriting";
import SDAAcknowledge from "./pages/sda/SDAAcknowledge";
import SDAQuote from "./pages/sda/SDAQuote";
import SDASignatures from "./pages/sda/SDASignatures";
import SDAPayment from "./pages/sda/SDAPayment";
import { SDAProvider } from "./pages/sda/SDAContext";
import RenewalLayout from "./pages/renewal/RenewalLayout";
import RenewalLanding from "./pages/renewal/RenewalLanding";
import RenewFlow from "./pages/renewal/RenewFlow";
import MoveOutFlow from "./pages/renewal/MoveOutFlow";
import NotSureFlow from "./pages/renewal/NotSureFlow";
import "react-phone-number-input/style.css";

const App: React.FC = () => (
  <AuthProvider>
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 pb-24 sm:px-6 lg:px-8">
        <Routes>
          <Route element={<PublicRoute />}>
            <Route path="/" element={<Home />} />
            <Route path="/start/:slug" element={<Home />} />
            <Route path="/login" element={<Login />} />
          </Route>

          {/* Auth-code login is NOT a public route — it must mount even
              when a user is already logged in so an admin's "Log in as
              User" link can swap accounts. LoginWithCode replaces the
              existing token on success. */}
          <Route path="/login-with-code/:code" element={<LoginWithCode />} />

          <Route path="/profile" element={<Profile />} />
          <Route path="/payment-methods" element={<PaymentMethods />} />
          <Route path="/faq" element={<FAQ />} />

          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/claim/:uuid" element={<ClaimDetails />} />
            <Route path="/claim/:uuid/pay" element={<ClaimPayment />} />
            <Route path="/policy/:uuid/renew" element={<PolicyRenewal />} />
            <Route
              path="/policy/:uuid/change/:requestUuid"
              element={<PolicyChangePayment />}
            />

            {/* Convenience alias so /renewals/test also lands on the demo. */}
            <Route
              path="/renewals/test"
              element={<Navigate to="/renewal/test" replace />}
            />

            {/* Renewal Concierge — keyed on policy.nextTerm.uuid, which is
                what the outreach emails link to. `/renewal/test` loads a
                fixture policy so the flow can be demoed without real data. */}
            <Route path="/renewal/:termUuid" element={<RenewalLayout />}>
              <Route index element={<RenewalLanding />} />
              <Route path="renew" element={<RenewFlow />} />
              <Route path="move-out" element={<MoveOutFlow />} />
              <Route path="not-sure" element={<NotSureFlow />} />
            </Route>
            <Route path="/application/:id" element={<Application />} />
            <Route path="/application/:id/payment" element={<PaymentPage />} />
            <Route path="/application/:id/basic" element={<BasicInformation />} />
            <Route path="/application/:id/housing" element={<HousingHistory />} />
            <Route
              path="/application/:id/identity"
              element={<IdentityVerification />}
            />
            <Route path="/application/:id/income" element={<IncomeSources />} />
            <Route path="/application/:id/financial" element={<Financial />} />
            <Route path="/application/:id/documents" element={<Documents />} />

            {/* SDA Flow Routes */}
            <Route path="/sda/:uuid" element={<SDAProvider />}>
              <Route index element={<Navigate to="acknowledge" replace />} />
              <Route path="identity" element={<SDAIdentity />} />
              <Route path="financial" element={<SDAFinancial />} />
              <Route path="underwriting" element={<SDAUnderwriting />} />
              <Route path="acknowledge" element={<SDAAcknowledge />} />
              <Route path="quote" element={<SDAQuote />} />
              <Route path="signatures" element={<SDASignatures />} />
              <Route path="payment" element={<SDAPayment />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
      <SupportChatWidget />
    </div>
  </AuthProvider>
);

export default App;
